/**
 * Service for interacting with the SLAM API.
 * SLAM is the item bank — it stores exercises/questions used in assessments.
 */

// In development the CRA dev server proxies /slam-api → https://slam.djammadev.com/api/v1
// to avoid CORS issues. In production, set REACT_APP_SLAM_API_URL to the full API URL.
const SLAM_BASE_URL =
  process.env.REACT_APP_SLAM_API_URL || 'https://slam.djammadev.com/api/v1';

const SLAM_TOKEN = process.env.REACT_APP_SLAM_TOKEN || '';

// ─── Types mirroring SLAM API models ──────────────────────────────────────────

export type SlamApprovalStatus = 'draft' | 'submitted' | 'approved' | 'rejected';

export interface SlamItemList {
  id: number;
  status: SlamApprovalStatus;
  domain?: string;
  skill?: string;
  grade?: string;
  title?: string;
  subtitle?: string;
  language?: string;
  difficulty?: number;
  source?: string;
  speakable?: boolean;
  expose_to_assessment?: boolean;
  created_at?: string;
  created_by?: string;
  last_modified_at?: string;
  last_modified_by?: string;
}

export interface SlamItem extends SlamItemList {
  template?: string;
  description?: string;
  response?: string;
  introduction?: string;
  indication?: string;
  alternative_descriptions?: string;
  alternative_responses?: string;
}

export interface SlamSearchRequest {
  query?: string;
  domain?: string;
  skill?: string;
  grade?: string;
  difficulty_min?: number;
  difficulty_max?: number;
  status?: SlamApprovalStatus;
  language?: string;
  source?: string;
  speakable?: boolean;
  expose_to_assessment?: boolean;
  page?: number;
  limit?: number;
  include_content?: boolean;
}

export interface SlamSearchResult {
  items: SlamItem[];
  total_count: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export interface SlamPicklist {
  id: number;
  type: string;
  value: string;
  label?: string;
  order?: number;
}
//
export function gradeToSlamGrade(grade?: string): string {
  if (!grade) return '';
  // 1 -> 1ère année
  // 2 -> 2ème année
  // 3ème année
  // 4ème année
  // 5ème année
  // 6ème année
  // 7ème année
  // 8ème année
  // 9ème année
  // 10ème année
  // 11ème année
  // 12ème année
  if (grade === '1') return '1ère année';
  return `${grade}ème année`;
}

// ─── Alternative version helpers ──────────────────────────────────────────────

/**
 * Parsed representation of one item version (description + response pair).
 */
export interface SlamItemVersion {
  /** 0 = version principale, 1+ = alternatives */
  index: number;
  description: string;
  response: string;
}

/**
 * Parse alternative_descriptions / alternative_responses fields.
 *
 * Structure:
 *   - Version 0 (main) : item.description + item.response
 *   - Version 1, 2, … : entries from item.alternative_descriptions + item.alternative_responses
 *
 * The alternative_* fields are stored as a JSON array of strings: ["alt1", "alt2", …]
 * Returns the main version at index 0, then alternatives.
 */
export function parseSlamVersions(item: SlamItem): SlamItemVersion[] {
  const parseAlternatives = (raw: string | undefined): string[] => {
    if (!raw) return [];
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(String).filter(s => s.trim() !== '');
      } catch { /* fall through */ }
    }
    // Fallback: treat as a single alternative value
    return trimmed ? [trimmed] : [];
  };

  const altDescriptions = parseAlternatives(item.alternative_descriptions);
  const altResponses = parseAlternatives(item.alternative_responses);

  const altCount = Math.max(altDescriptions.length, altResponses.length);

  const versions: SlamItemVersion[] = [
    {
      index: 0,
      description: item.description ?? '',
      response: item.response ?? '',
    },
  ];

  for (let i = 0; i < altCount; i++) {
    versions.push({
      index: i + 1,
      description: altDescriptions[i] ?? '',
      response: altResponses[i] ?? '',
    });
  }

  return versions;
}

// ─── HTTP helpers ──────────────────────────────────────────────────────────────

function getHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (SLAM_TOKEN) {
    headers['Authorization'] = `Bearer ${SLAM_TOKEN}`;
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`SLAM API error ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ─── Service ──────────────────────────────────────────────────────────────────

/**
 * Convert a SlamItem into an AssessmentItem compatible with Education's AssessmentPanel.
 * All template variables / <eval> / <object> are evaluated here so that each selected
 * version is a fixed snapshot.
 * @param item     The SLAM item
 * @param version  Optional specific version (from parseSlamVersions). Defaults to version 0.
 */
export function toAssessmentItem(
  item: SlamItem,
  version?: SlamItemVersion,
): {
  id: string;
  type: string;
  prompt: string;
  answer: string;
  slam_item_id: number;
  slam_version_index: number;
  slam_domain?: string;
  slam_title?: string;
  slam_subtitle?: string;
  slam_skill?: string;
  slam_grade?: string;
  slam_difficulty?: number;
  slam_description?: string;
  slam_indication?: string;
  slam_introduction?: string;
} {
  const v = version ?? parseSlamVersions(item)[0];

  // Evaluate template variables and dynamic tags once, so this version is a snapshot.
  const { compileSlamContent } = require('../utils/slamTemplateCompiler') as typeof import('../utils/slamTemplateCompiler');
  const compiled = compileSlamContent({
    template: item.template,
    description: v.description || '',
    response: v.response || '',
    introduction: item.introduction || '',
    indication: item.indication || '',
  });

  return {
    id: `slam_${item.id}_v${v.index}`,
    type: 'slam_item',
    prompt: '',
    answer: compiled.response,
    slam_item_id: item.id,
    slam_version_index: v.index,
    slam_domain: item.domain,
    slam_title: item.title,
    slam_subtitle: item.subtitle,
    slam_skill: item.skill,
    slam_grade: item.grade,
    slam_difficulty: item.difficulty,
    slam_description: compiled.description,
    slam_indication: compiled.indication,
    slam_introduction: compiled.introduction,
  };
}

export const slamService = {
  /**
   * Search items with filters (grade, domain, skill, difficulty, …).
   * Returns only APPROVED items by default.
   */
  async searchItems(params: SlamSearchRequest = {}): Promise<SlamSearchResult> {
    const body: SlamSearchRequest = {
      status: 'approved',
      page: 1,
      limit: 20,
      include_content: true,
      ...params,
      expose_to_assessment: true,
    };
    const res = await fetch(`${SLAM_BASE_URL}/items/search`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse<SlamSearchResult>(res);
  },

  /**
   * Fetch a single item by ID (full model with alternatives).
   */
  async getItem(itemId: number): Promise<SlamItem> {
    const res = await fetch(`${SLAM_BASE_URL}/items/${itemId}`, {
      headers: getHeaders(),
    });
    return handleResponse<SlamItem>(res);
  },

  /**
   * Fetch multiple items at once.
   */
  async getItemsBatch(itemIds: number[]): Promise<SlamItem[]> {
    const res = await fetch(`${SLAM_BASE_URL}/items/details/batch`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(itemIds),
    });
    return handleResponse<SlamItem[]>(res);
  },

  /**
   * Get all items in a collection.
   */
  async getCollectionItems(collectionId: string): Promise<SlamItem[]> {
    const res = await fetch(`${SLAM_BASE_URL}/items/collection/${collectionId}`, {
      headers: getHeaders(),
    });
    return handleResponse<SlamItem[]>(res);
  },

  /**
   * Fetch picklist entries (domains, skills, grades, …).
   */
  async getPicklists(type?: string): Promise<SlamPicklist[]> {
    const url = new URL(`${SLAM_BASE_URL}/picklists/`);
    if (type) url.searchParams.set('type', type);
    url.searchParams.set('limit', '200');
    const res = await fetch(url.toString(), { headers: getHeaders() });
    return handleResponse<SlamPicklist[]>(res);
  },

  toAssessmentItem,
};
